/* eslint-disable no-param-reassign,react/jsx-props-no-spreading */
import React, { useState } from "react";
import { Button, Row, Col, Table, Space } from "antd";
import "../../styles/filters-form.less";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { omit } from "lodash";
import { NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../..";
import AuthService from "../../services/AuthService";
import { formatDate } from "../../lib/utils";
import { exportToXLS } from "../../store/slices/xlsExport";
import { handleReTariffication } from "../../store/slices/rejected-traffic";
import { toHttpFormatEndOfDay, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_BILLING_TRAFFIC_WRITE } from "../../lib/const/permissions";
import SpanNumberFormat from "../common/SpanNumberFormat";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { RejectedTrafficSearchFilters } from "./RejectedTrafficSearchFilters/RejectedTrafficSearcFilters";
import "./RejectedTraffic.less";

const RejectedTraffic = ({
  form,
  isLoadData,
  data,
  rejectedTrafficsLoading,
  fromDateValue,
  toDateValue,
  getDate,
  handleSearchRejectsTraffic,
  resetSearchRejectsTraffic,
  rejectedTrafficSearchParams,
}) => {
  const dispatch = useAppDispatch();
  const { tarifficationLoading } = useAppSelector((state) => state.rejected);
  const trafficStatuses = useAppSelector((state) => state.rtkDictionaries.trafficStatus);
  const { companyIdentityAttribute } = useAppSelector((state) => state.tenants.tenantProperties);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formValues, setFormValues] = useState("");
  const exportStatus = useAppSelector((state) => state.xlsExport.exportToXLSLoading);
  const [filtersCollapseState, setFiltersCollapsState] = useState("unfolded");

  const resetSearchPaymentsAndResetSetSelectedRowKeys = () => {
    resetSearchRejectsTraffic();
    setSelectedRowKeys([]);
  };

  const getTableHeight = () => (filtersCollapseState === "unfolded" ? 400 : 560);

  const renderFooter = () => {
    const handleExport = async () => {
      const selectedRejectedTraffics =
        isLoadData && data ? data.filter((item) => selectedRowKeys.includes(item.id)) : "";
      const { display_name: author } = jwtDecode(AuthService.getCurrentUser());

      const exportedData = {
        fileName: `Отсев_${formatDate.toDisplayFormat(moment())}`,
        title: "Выгрузка данных в формате xls",
        description: "Выгрузка данных",
        periodFrom: moment(fromDateValue).format("YYYY-MM-DD"),
        periodTo: moment(toDateValue).format("YYYY-MM-DD"),
        author,
        header: [
          { name: "subscriberName", title: "Аккаунт", type: "String" },
          { name: "amount", title: "Количество", type: "Double" },
          { name: "subscriber", title: "Технический ID", type: "String" },
          { name: "role", title: "Направление", type: "String" },
          { name: "statusName", title: "Причина отсева", type: "String" },
        ],
        data: await selectedRejectedTraffics.map((item) => {
          let trStatus;
          if (trafficStatuses) {
            const check = trafficStatuses.find((item2) => item2.id === item.serviceUsageStatusId);
            if (check) {
              trStatus = check.name;
            } else {
              trStatus = "";
            }
          }
          return {
            subscriberName: item?.subscriberName,
            amount: item?.amount,
            subscriber: item?.subscriber,
            role:
              item?.role === "SENDER" ? "Отправитель" : item?.role === "RECIPIENT" && "Получатель",
            statusName: trStatus,
          };
        }),
      };
      dispatch(exportToXLS(exportedData));
    };

    const handleTariff = async () => {
      const selectedRejectedTraffics =
        data && data.filter((item) => selectedRowKeys.includes(item.id));

      const senderSubscribersIdes =
        selectedRejectedTraffics &&
        selectedRejectedTraffics
          .filter((item) => item.subscriber && item.role === "SENDER")
          .map((item) => item.subscriber && item.subscriber);

      const recipientSubscribersIdes =
        selectedRejectedTraffics &&
        selectedRejectedTraffics
          .filter((item) => item.subscriber && item.role === "RECIPIENT")
          .map((item) => item.subscriber && item.subscriber);

      const subscribersIdes =
        selectedRejectedTraffics &&
        selectedRejectedTraffics
          .filter((item) => item.subscriber && item.role === "ANY")
          .map((item) => item.subscriber && item.subscriber);

      const exportedData = {
        senderSubscribers: senderSubscribersIdes,
        recipientSubscribers: recipientSubscribersIdes,
        subscribers: subscribersIdes,
        dateFrom: fromDateValue ? toHttpFormatStartOfDay(fromDateValue) : "",
        dateTo: toDateValue ? toHttpFormatEndOfDay(toDateValue) : "",
        excludeServiceUsageStatusIds: [0],
      };
      await dispatch(handleReTariffication(exportedData));
      handleSearchRejectsTraffic(formValues);
    };

    if (hasAccess(MENU_BILLING_TRAFFIC_WRITE)) {
      return (
        <Space className="buttons-container">
          <Button
            type="primary"
            disabled={selectedRowKeys.length === 0}
            loading={checkIsLoading(exportStatus)}
            onClick={handleExport}
          >
            Выгрузить в XLS
          </Button>
          <Button
            type="primary"
            disabled={selectedRowKeys.length === 0}
            loading={checkIsLoading(tarifficationLoading)}
            onClick={handleTariff}
          >
            Дотарификация
          </Button>
        </Space>
      );
    }
    return null;
  };

  const tableData =
    isLoadData &&
    data &&
    data.map((reject) => ({
      key: reject?.id,
      subscriberId: reject?.subscriberId,
      subscriberName: reject?.subscriberName,
      amount: reject?.amount,
      subscriber: reject?.subscriber,
      roleName:
        reject?.role === "SENDER" ? "Отправитель" : reject?.role === "RECIPIENT" && "Получатель",
      role: reject?.role,
      serviceUsageStatusId: reject.serviceUsageStatusId,
      statusName:
        trafficStatuses &&
        trafficStatuses.find((item) => item.id === reject.serviceUsageStatusId)?.name,
    }));

  const [columns, setColumns] = useState([
    {
      title: "Аккаунт",
      dataIndex: "subscriberName",
      key: "subscriberName",
    },
    {
      title: "Количество",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => (
        <>
          {record?.subscriber ? (
            <NavLink
              className="reject-traffic-link"
              // to={`/rejected-traffic-detail/?search_id=${record.subscriber}&role=${record.role}&=status=${record.serviceUsageStatusId}`}
              to={`/rejected-traffic-detail/${record.subscriber}`}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem(
                  `${record.subscriber}`,
                  JSON.stringify({
                    serviceUsageStatusId: record.serviceUsageStatusId,
                    // subscriberId: record.subscriberId,
                    subscriber: record.subscriber,
                    role: record.role,
                    fromDate: `${moment(fromDateValue)
                      .utcOffset(0)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS")}Z`,
                    toDate: `${moment(toDateValue)
                      .utcOffset(0)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS")}Z`,
                  })
                );
              }}
            >
              {SpanNumberFormat({ amount, fractSize: 0 })}
            </NavLink>
          ) : (
            <div style={{ padding: "0 5px" }}>{SpanNumberFormat({ amount, fractSize: 0 })}</div>
          )}
        </>
      ),
    },
    {
      title: "Технический ID",
      dataIndex: "subscriber",
      key: "subscriber",
    },
    {
      title: "Направление",
      dataIndex: "roleName",
      key: "roleName",
    },
    {
      title: "Причина отсева",
      dataIndex: "statusName",
      key: "statusName",
    },
  ]);

  const onSearch = (values) => {
    if (values) {
      if (values["organization.property.stringValue"]) {
        values["organization.property.typeId"] =
          companyIdentityAttribute && companyIdentityAttribute;
      }
      values.fromDate = toHttpFormatStartOfDay(values.rangeDate[0]);
      values.toDate = toHttpFormatEndOfDay(values.rangeDate[1]);
      values.rangeDate = "";
    }
    // Параметры subscribersThroughSubscriberIds(Клиент) и subscribers(Тех.-ID) для целей поиска взаимоисключащие
    // и с учетом того, что по Тех.-ID подтянгивается клиент, Тех.-ID не передаем никогда
    handleSearchRejectsTraffic(omit(values, "subscribers"));
    setSelectedRowKeys([]);
    setFormValues(values);
  };

  return (
    <div className="container_height">
      <div className="inner-row" style={{ height: "50px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Массовые операции по работе с отсевом</h1>
          </Col>
        </Row>
      </div>
      <RejectedTrafficSearchFilters
        getDate={getDate}
        resetSearchPaymentsAndResetSetSelectedRowKeys={
          resetSearchPaymentsAndResetSetSelectedRowKeys
        }
        rejectedTrafficsLoading={rejectedTrafficsLoading}
        defaultSearchParams={rejectedTrafficSearchParams}
        form={form}
        onSearch={onSearch}
        filtersCollapseState={filtersCollapseState}
        setFiltersCollapsState={setFiltersCollapsState}
      />
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns
          tableName="rejected-traffic-table"
          columns={columns}
          setColumns={setColumns}
        />
      </Row>
      <Table
        size="small"
        columns={columns.filter(({ hidden }) => !hidden)}
        dataSource={tableData}
        pagination={false}
        loading={checkIsLoading(rejectedTrafficsLoading)}
        scroll={{ y: getTableHeight() }}
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
          },
        }}
        onRow={(record) => ({
          onClick: () => {
            if (record.subscriberId) {
              // showAccById(record.subscriberId);
              window.open(`/accounts/${record.subscriberId}/info`);
            }
          },
        })}
      />

      {renderFooter()}
    </div>
  );
};

export default RejectedTraffic;
